/**
 * usePasskeySignIn — passkey サインイン導線を駆動する hook（共有版）。
 *
 * `AuthProvider` 経由の `signInWithPasskey()`（username-less / discoverable credential）を
 * 実行し、pending / エラー状態を管理する。成功時は認証状態が AuthProvider 側で更新済みの
 * 状態で `onSuccess(response)` を呼ぶ（遷移方法はアプリが決める＝ハードコード redirect なし）。
 *
 * - キャンセル/タイムアウト（`NotAllowedError`）: `error=null` へ戻し、エラー表示も
 *   `onError` 通知も行わず再試行可能にする。
 * - その他エラー: `toPasskeyErrorMessage` で日本語メッセージへ変換し `error` に設定、
 *   `onError(err)` を通知する。既存パスワードログインの利用可能性には影響しない。
 */
import React from "react";
import { toPasskeyErrorMessage } from "../utils/passkey-error";
import { useAuth } from "./useAuth";
/**
 * passkey サインイン hook。
 */
export function usePasskeySignIn(options) {
    const { signInWithPasskey } = useAuth();
    const onSuccess = options?.onSuccess;
    const onError = options?.onError;
    const [isPending, setIsPending] = React.useState(false);
    const [error, setError] = React.useState(null);
    const signIn = React.useCallback(async () => {
        setIsPending(true);
        setError(null);
        try {
            const response = await signInWithPasskey();
            setIsPending(false);
            onSuccess?.(response);
        }
        catch (err) {
            const { message, cancelled } = toPasskeyErrorMessage(err);
            // キャンセル/タイムアウトはエラー表示も onError 通知もせず、再試行可能へ戻す。
            setError(cancelled ? null : message);
            setIsPending(false);
            if (!cancelled) {
                onError?.(err);
            }
        }
    }, [signInWithPasskey, onSuccess, onError]);
    return { isPending, error, signIn };
}
